'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import {
  AgentExecutionActionsCell,
  AgentExecutionCreatedAtCell,
  AgentExecutionDurationCell,
  AgentExecutionObjectiveCell,
  AgentExecutionSelectionCell,
  AgentExecutionStatusCell,
  AgentExecutionsSelectAllHeader,
} from './AgentExecutionsTableCells';

const SELECT_COLUMN_WIDTH = 40;
const ACTIONS_COLUMN_WIDTH = 80;
const preserveServerSortOrder = () => 0;

const renderInCellFrame =
  (render: (execution: AgentExecutionDto) => ReactNode) =>
  (params: ICellRendererParams<AgentExecutionDto>) =>
    params.data ? (
      <Box h="full" display="flex" alignItems="center" minW={0}>
        {render(params.data)}
      </Box>
    ) : null;

export function useAgentExecutionsTableColumnDefs(): ColDef<AgentExecutionDto>[] {
  const t = useTranslations('agentExecutions.columns');

  return useMemo<ColDef<AgentExecutionDto>[]>(
    () => [
      {
        colId: 'select',
        headerName: '',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponent: AgentExecutionsSelectAllHeader,
        cellRenderer: (params: ICellRendererParams<AgentExecutionDto>) =>
          params.data ? (
            <AgentExecutionSelectionCell execution={params.data} />
          ) : null,
      },
      {
        colId: 'objective',
        headerName: t('objective'),
        flex: 2,
        minWidth: 180,
        sortable: false,
        cellRenderer: renderInCellFrame((execution) => (
          <AgentExecutionObjectiveCell execution={execution} />
        )),
      },
      {
        colId: 'status',
        headerName: t('status'),
        flex: 1,
        minWidth: 120,
        maxWidth: 180,
        sortable: true,
        comparator: preserveServerSortOrder,
        cellRenderer: renderInCellFrame((execution) => (
          <AgentExecutionStatusCell execution={execution} />
        )),
      },
      {
        colId: 'duration',
        headerName: t('duration'),
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        sortable: true,
        comparator: preserveServerSortOrder,
        cellRenderer: renderInCellFrame((execution) => (
          <AgentExecutionDurationCell execution={execution} />
        )),
      },
      {
        colId: 'createdAt',
        headerName: t('created'),
        flex: 1,
        minWidth: 140,
        maxWidth: 220,
        sortable: true,
        comparator: preserveServerSortOrder,
        cellRenderer: renderInCellFrame((execution) => (
          <AgentExecutionCreatedAtCell execution={execution} />
        )),
      },
      {
        colId: 'actions',
        headerName: '',
        width: ACTIONS_COLUMN_WIDTH,
        minWidth: ACTIONS_COLUMN_WIDTH,
        maxWidth: ACTIONS_COLUMN_WIDTH,
        pinned: 'right',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<AgentExecutionDto>) =>
          params.data ? (
            <AgentExecutionActionsCell execution={params.data} />
          ) : null,
      },
    ],
    [t],
  );
}
